import { useNavigate } from 'react-router-dom'

import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import { PagesDeleteHandbookPageDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { usePagesContext } from 'modules/pages/lib/hooks/usePagesContext'

interface DeletePageDialogProps {
  pageId: number
  onClose: () => void
  redirect?: boolean
  folderType?: string
  onDeleteSuccess?: () => void
}

export const DeletePageDialog = ({
  pageId,
  onClose,
  redirect = false,
  folderType,
  onDeleteSuccess,
}: DeletePageDialogProps) => {
  const navigate = useNavigate()
  const { canEditPages } = usePagesContext()
  const { navigation } = useNavigation()

  const [deleteMeeting] = useMutation(PagesDeleteHandbookPageDocument, {
    extractorFn: (data) => data.handbookPagesMutations.deleteHandbookpagesPage,
  })

  const handleConfirmDelete = async () => {
    if (!canEditPages) return

    const result = await deleteMeeting({
      variables: {
        id: pageId,
      },
      update(cache) {
        cache.evict({ id: cache.identify({ id: pageId, __typename: 'HandbookPagesPage' }) })
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      onDeleteSuccess?.()
      app.setFlash({
        type: 'correct',
        message: i18n.t('pages.page.delete_page_dialog.success'),
      })
      if (redirect && folderType) {
        navigate(
          navigation.pages.folder.fullPath({
            folderSlug: folderType,
          })
        )
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }

    onClose()
  }

  return (
    <Dialog
      onClose={onClose}
      actions={{
        primary: {
          icon: Delete,
          label: i18n.t('common.delete'),
          onClick: () => handleConfirmDelete(),
          variant: 'critical',
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: onClose,
        },
      }}
      header={{
        description: i18n.t('pages.page.delete_page_dialog.description'),
        title: i18n.t('pages.page.delete_page_dialog.title'),
        type: 'critical',
      }}
      open={!!pageId}
    />
  )
}
